import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { chatbot_name, assistantId } from '../../config';
import useChatThread from "../../hooks/useChatThread";
import useUser from "../../hooks/useUser";
import ChatUI from "./ChatUI";
import "./ChatApp.css";
import useSendMessage from "../../hooks/useSendMessage.js";
import useSaveMessages from "../../hooks/useSaveMessages.js";

const animationVariants = {
  hidden: {
    opacity: 0,
    width: 70,
    height: 70,
    borderRadius: "75px",
    right: 40,
    bottom: 20,
  },
  visible: {
    opacity: 1,
    width: "calc(100% - 80px)",
    height: 600,
    borderRadius: "12px",
    right: 40,
    bottom: 20,
    transition: {
      duration: 0.4,
      ease: "easeInOut",
    },
  },
  exit: {
    opacity: 0,
    width: 70,
    height: 70,
    borderRadius: "75px",
    right: 40,
    bottom: 20,
    transition: {
      duration: 0.3,
      ease: "easeInOut",
    },
  },
};

const ChatApp = ({ functionCallHandler = () => Promise.resolve("") }) => {
  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState([]);

  const [input, setInput] = useState("");

  const messagesEndRef = useRef(null);
  const lastSavedMessageIdRef = useRef(null);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const navigate = useNavigate();

  const { threadId } = useChatThread();
  const { isAdmin } = useUser();
  const { sendMessage, isLoading, error, statusMessage, analyzingFiles, newMessages } = useSendMessage(threadId, assistantId, setMessages);
  const { saveMessages } = useSaveMessages(threadId)
  const inputRef = useRef(null);

  const toggleSound = () => {
    setIsSoundEnabled((prev) => !prev);
  };

  const openThreads = () => {
    navigate("/chat-threads")
    setIsOpen(false)
  };

  useEffect(() => {
    if (isOpen && !messages.length) {
      setTimeout(() => {
        setMessages([{
          id: "welcome",
          role: "assistant",
          content: `Hi, I’m Eva <br/><br/>
                    Your go-to for KPS info, here to help you find what you need — just message me and I’ll point you in the right direction.`,
        }]);
        inputRef?.current?.focus();

      }, 1000);
    }
  }, [isOpen, messages])

  const restartChat = () => {
    setMessages([{
      id: "welcome",
      role: "assistant",
      content: `Hi, I’m Eva <br/><br/>
                Your go-to for KPS info, here to help you find what you need — just message me and I’ll point you in the right direction.`,
    }]);
    setInput("");
  };

  useEffect(() => {

    const playNotificationSound = () => {
      if (isSoundEnabled) {
        const audio = new Audio("/notification.mp3"); // Replace with your sound file path
        audio.play();
      }
    };


    const lastMessage = messages[messages.length - 1];
    if (
      messages.length > 1 &&
      lastMessage.role === "assistant" &&
      lastMessage.id !== lastSavedMessageIdRef.current
    ) {
      lastSavedMessageIdRef.current = lastMessage.id;
      playNotificationSound();
      inputRef?.current?.focus();
      saveMessages(messages);
    }

    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isSoundEnabled, saveMessages]);



  const handleSendMessage = () => {
    if (input.trim()) {
      sendMessage(input.trim());
      setInput("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <ChatUI {...{
      isOpen,
      setIsOpen,
      chatbot_name,
      toggleSound,
      isSoundEnabled,
      isAdmin,
      openThreads,
      restartChat,
      error,
      messages,
      newMessages,
      isLoading,
      input,
      setInput,
      handleKeyPress,
      handleSendMessage,
      threadId,
      messagesEndRef,
      animationVariants,
      analyzingFiles,
      statusMessage,
      inputRef
    }} />
  );
};

export default ChatApp;
